/**
 * AgentSessionHistoryView — shared run-history panel for all session-based agents
 * (Data Masking, Semantic Intelligence, Metadata Catalog, Backlog Architect).
 *
 * Fetches AgentSession records for the project+type via the project activity
 * endpoint and renders a list with date, label, status, and a "Resume" action.
 */

import { useEffect, useState } from "react";
import {
  Loader2,
  RefreshCcw,
  Play,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { apiv2, type AgentSessionSummary } from "../../apiv2";

interface AgentSessionHistoryViewProps {
  projectId: string;
  sessionType: string;
  agentLabel: string;
  onResume: (sessionId: string) => void;
  onNewSession: () => void;
}

function statusConfig(status: string) {
  switch (status) {
    case "active":
      return {
        label: "Active",
        icon: <Play size={10} />,
        cls: "bg-sky-500/15 text-sky-300 border-sky-500/25",
      };
    case "completed":
      return {
        label: "Completed",
        icon: <CheckCircle2 size={10} />,
        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
      };
    case "expired":
      return {
        label: "Expired",
        icon: <AlertTriangle size={10} />,
        cls: "bg-white/5 text-white/40 border-white/10",
      };
    default:
      return {
        label: status,
        icon: <Clock size={10} />,
        cls: "bg-white/5 text-white/55 border-white/10",
      };
  }
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function AgentSessionHistoryView({
  projectId,
  sessionType,
  agentLabel,
  onResume,
  onNewSession,
}: AgentSessionHistoryViewProps) {
  const [sessions, setSessions] = useState<AgentSessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const activity = await apiv2.getProjectActivity(projectId);
      setSessions(
        (activity.sessions ?? [])
          .filter((s) => s.session_type === sessionType)
          .sort(
            (a, b) =>
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime(),
          ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [projectId, sessionType]);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-white">
            {agentLabel} — Run History
          </h2>
          <p className="text-sm text-white/50 mt-0.5">
            {sessions.length} session{sessions.length !== 1 ? "s" : ""} for this
            project
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
            title="Refresh"
          >
            <RefreshCcw size={14} />
          </button>
          <button
            onClick={onNewSession}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-500/15 text-brand-300 hover:bg-brand-500/25 border border-brand-500/25 transition-colors"
          >
            + New Session
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-white/40">
          <Loader2 size={20} className="animate-spin mr-2" />
          <span className="text-sm">Loading history…</span>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/25 text-sm text-red-300 mb-4">
          {error}
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Clock size={32} className="text-white/20 mb-3" />
          <p className="text-sm text-white/50">
            No sessions yet for this project.
          </p>
          <button
            onClick={onNewSession}
            className="mt-4 px-4 py-2 rounded-lg text-xs font-medium bg-brand-500/15 text-brand-300 hover:bg-brand-500/25 border border-brand-500/25 transition-colors"
          >
            Start First Session
          </button>
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <div
          className="rounded-xl border border-white/10 overflow-hidden"
          style={{ background: "rgba(28,28,42,0.45)" }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 bg-white/[0.02]">
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-white/40 uppercase tracking-wide">
                  Date
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-white/40 uppercase tracking-wide">
                  Label
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-white/40 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-white/40 uppercase tracking-wide">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sessions.map((s) => {
                const sc = statusConfig(s.status);
                const canResume =
                  s.status === "active" || s.status === "completed";
                return (
                  <tr
                    key={s.id}
                    className="hover:bg-white/[0.025] transition-colors"
                  >
                    <td className="px-4 py-3 text-[12px] text-white/60 whitespace-nowrap">
                      {formatDate(s.updated_at)}
                    </td>
                    <td
                      className="px-4 py-3 text-[12px] text-white/80 max-w-[260px] truncate"
                      title={s.label || undefined}
                    >
                      {s.label || (
                        <span className="text-white/30 italic">
                          unnamed session
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${sc.cls}`}
                      >
                        {sc.icon} {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canResume ? (
                        <button
                          onClick={() => onResume(s.id)}
                          className="text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
                        >
                          {s.status === "active" ? "Resume →" : "View →"}
                        </button>
                      ) : (
                        <span className="text-[11px] text-white/25">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
